import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import client from "../../sanity/sanityClient";
import AgentCard from "../home/AgentCard";
import { PortableText } from "@portabletext/react";

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      client
        .fetch(
          `*[_type == "post" && slug.current == $slug][0]{
             title,
          subtitle,
          "image": mainImage.asset->url,
          readTime,
          date, 
          content, 
          author  
          }`,
          { slug }
        )
        .then((data) => {
          setBlog(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching blog:", error);
          setLoading(false);
        });
    }
  }, [slug]);

  function formatDate(date) {
    if (!date) return "Date not available";
    const d = new Date(date);
    return d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ (\w{3}) /, " $1, ");
  }

  // Custom components for Portable Text - ENHANCED VERSION
  const portableTextComponents = {
    block: {
      h1: ({ children }) => (
        <h1 className="text-5xl font-extrabold mt-12 mb-6 text-gray-900 leading-tight">
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 className="text-4xl font-bold mt-10 mb-5 text-gray-900 leading-tight">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-3xl font-bold mt-8 mb-4 text-gray-800">
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-2xl font-semibold mt-6 mb-3 text-gray-800">
          {children}
        </h4>
      ),
      normal: ({ children }) => (
        <p className="text-base leading-relaxed mb-6 text-gray-700">
          {children}
        </p>
      ),
      blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-blue-500 bg-blue-50 pl-6 py-4 italic my-8 text-gray-800 text-lg">
          {children}
        </blockquote>
      ),
    },
    list: {
      bullet: ({ children }) => (
        <ul className="list-disc list-outside space-y-3 mb-8 ml-8 text-gray-700 text-base">
          {children}
        </ul>
      ),
      number: ({ children }) => (
        <ol className="list-decimal list-outside space-y-3 mb-8 ml-8 text-gray-700 text-base">
          {children}
        </ol>
      ),
    },
    listItem: {
      bullet: ({ children }) => (
        <li className="leading-relaxed pl-2">{children}</li>
      ),
      number: ({ children }) => (
        <li className="leading-relaxed pl-2">{children}</li>
      ),
    },
    marks: {
      strong: ({ children }) => (
        <strong className="font-bold text-gray-900">{children}</strong>
      ),
      em: ({ children }) => (
        <em className="italic text-gray-800">{children}</em>
      ),
      code: ({ children }) => (
        <code className="bg-gray-200 px-2 py-1 rounded text-sm font-mono text-red-600">
          {children}
        </code>
      ),
      link: ({ children, value }) => (
        <a
          href={value?.href}
          className="text-blue-600 hover:text-blue-800 underline font-medium"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      ),
    },
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <p className="text-center text-gray-600">Loading blog post...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <p className="text-center text-gray-600">Blog post not found.</p>
      </div>
    );
  }

  return (
    <div>
      <section className="max-w-4xl mx-auto px-4 py-16">
        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-semibold ">
          {blog.title}
        </h1>
          {blog.author && (
              <p className="mt-4 text-gray-700 font-medium">
                  By {blog.author}
              </p>
          )}
          {blog.subtitle && (
              <p className="mt-3 text-gray-600 text-lg max-w-3xl">
                  {blog.subtitle}
              </p>
          )}

          <div className="flex items-center justify-between mt-6 text-gray-600 text-sm border-t-2 pt-3">
              <span>{blog.readTime || "Read time not available"}</span>
              <span>{formatDate(blog.date)}</span>
          </div>


        {/* Main Image */}
        {blog.image && (
          <div className="relative mt-8 w-full h-[420px] md:h-[500px] rounded-3xl overflow-hidden">
            {/* Blurred background */}
            <div
              className="absolute inset-0 bg-center bg-no-repeat bg-cover filter blur-xl scale-110"
              style={{ backgroundImage: `url(${blog.image})` }}
            ></div>

            {/* Full image on top */}
            <div className="relative flex items-center justify-center h-full">
              <img
                src={blog.image}
                alt={blog.title}
                className="max-h-full max-w-full object-contain rounded-2xl"
              />
            </div>
          </div>
        )}

        {/* Article Content */}
        <article className="prose prose-lg max-w-none mt-12">
          {blog.content && blog.content.length > 0 ? (
            <PortableText 
              value={blog.content} 
              components={portableTextComponents} 
            />
          ) : (
            <p className="text-gray-500 italic">No content available.</p>
          )}
        </article>
      </section>
      <AgentCard />
    </div>
  );
};

export default BlogDetail;